// `ProposalsPanel` — Phase 2d for link candidates, extended in Phase 3b
// with a children-mode discriminator for decompose proposals. The two
// shapes share the panel chrome and per-row Accept/Skip toggle; the
// children mode swaps the row content to show the proposed title +
// INVEST chips instead of a target iid + edge type.

import type { JSX } from "react";

import type {
  AcceptedLink,
  DecomposeChild,
  LinkCandidateRow,
} from "@loom/shared/dashboard";

import { useT, usePlural } from "../../i18n/index.js";

import { Icon } from "./Icon";

export type ProposalsPanelProps =
  | LinksModeProps
  | ChildrenModeProps;

interface LinksModeProps {
  readonly mode?: "links";
  readonly candidates: ReadonlyArray<LinkCandidateRow>;
  readonly accepted: ReadonlyArray<AcceptedLink>;
  readonly loading: boolean;
  readonly methodologySkipped: boolean;
  readonly onToggle: (link: AcceptedLink) => void;
}

interface ChildrenModeProps {
  readonly mode: "children";
  readonly children: ReadonlyArray<DecomposeChild>;
  readonly accepted: ReadonlyArray<number>;
  readonly loading: boolean;
  readonly methodologySkipped: boolean;
  readonly onToggleChild: (index: number) => void;
}

const PAGE_SIZE = 6;

const isAccepted = (
  accepted: ReadonlyArray<AcceptedLink>,
  iid: number,
): boolean => accepted.some((a) => a.targetIid === iid);

export const ProposalsPanel = (
  props: ProposalsPanelProps,
): JSX.Element | null => {
  if (props.methodologySkipped) return null;
  if (props.mode === "children") {
    return <ChildrenPanel {...props} />;
  }
  return <LinksPanel {...(props as LinksModeProps)} />;
};

const LinksPanel = ({
  candidates,
  accepted,
  loading,
  onToggle,
}: LinksModeProps): JSX.Element | null => {
  const t = useT();
  if (candidates.length === 0 && !loading) return null;
  const visible = candidates.slice(0, PAGE_SIZE);
  return (
    <div className="proposals-panel">
      <div className="proposals-panel-head">
        <Icon name="link" size={12} />
        <span className="proposals-panel-label">{t("dashboard.proposals.relatedWork")}</span>
        <span className="proposals-panel-sub">
          {loading
            ? t("dashboard.proposals.looking")
            : t(
                candidates.length === 1
                  ? "dashboard.proposals.candidatesOne"
                  : "dashboard.proposals.candidatesMany",
                { count: candidates.length },
              )}
        </span>
      </div>
      <div className="proposals-panel-rows">
        {visible.map((c) => {
          const on = isAccepted(accepted, c.iid);
          return (
            <div key={c.iid} className={`proposals-row ${on ? "on" : ""}`}>
              <span className="proposals-key">#{c.iid}</span>
              <span className="proposals-title">{c.title}</span>
              <span className="proposals-edge">
                {c.edgeType.replace(/_/g, " ")}
              </span>
              <button
                className={`proposals-toggle ${on ? "on" : ""}`}
                onClick={() =>
                  onToggle({ targetIid: c.iid, edgeType: c.edgeType })
                }
                title={on ? t("dashboard.proposals.skipLink") : t("dashboard.proposals.acceptLink")}
              >
                <Icon name={on ? "check" : "plus"} size={12} />
                {on ? t("dashboard.proposals.linked") : t("dashboard.proposals.link")}
              </button>
            </div>
          );
        })}
      </div>
      {candidates.length > PAGE_SIZE && (
        <div className="proposals-panel-foot">
          {t("dashboard.proposals.andMore", { count: candidates.length - PAGE_SIZE })}
        </div>
      )}
    </div>
  );
};

const ChildrenPanel = ({
  children,
  accepted,
  loading,
  onToggleChild,
}: ChildrenModeProps): JSX.Element | null => {
  const t = useT();
  const tn = usePlural();
  if (children.length === 0 && !loading) return null;
  return (
    <div className="proposals-panel proposals-panel-children">
      <div className="proposals-panel-head">
        <Icon name="scissors" size={12} />
        <span className="proposals-panel-label">{t("dashboard.proposals.subIssues")}</span>
        <span className="proposals-panel-sub">
          {loading
            ? t("dashboard.proposals.splitting")
            : tn("plural.children", children.length)}
        </span>
      </div>
      <div className="proposals-panel-rows">
        {children.map((child, i) => {
          const on = accepted.includes(i);
          return (
            <div
              key={`${child.parentIid}-${i}`}
              className={`proposals-row proposals-row-child ${on ? "on" : ""}`}
            >
              <span className="proposals-title">{child.title}</span>
              <span className="proposals-invest">
                <InvestChip
                  label="S"
                  verdict={child.investSummary.small}
                  title={t("dashboard.proposals.invest.small")}
                />
                <InvestChip
                  label="I"
                  verdict={child.investSummary.independent}
                  title={t("dashboard.proposals.invest.independent")}
                />
                <InvestChip
                  label="T"
                  verdict={child.investSummary.testable}
                  title={t("dashboard.proposals.invest.testable")}
                />
              </span>
              <button
                className={`proposals-toggle ${on ? "on" : ""}`}
                onClick={() => onToggleChild(i)}
                title={on ? t("dashboard.proposals.skipChild") : t("dashboard.proposals.createChild")}
              >
                <Icon name={on ? "check" : "plus"} size={12} />
                {on ? t("dashboard.proposals.selected") : t("dashboard.proposals.select")}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

interface InvestChipProps {
  readonly label: string;
  readonly verdict: "ok" | "warn" | "fail";
  readonly title: string;
}

const InvestChip = ({ label, verdict, title }: InvestChipProps): JSX.Element => (
  <span className={`invest-chip invest-${verdict}`} title={title}>
    {label}
  </span>
);
